import { css } from '@emotion/react';
import {
  faPlus, faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import {
  type MouseEventHandler, useCallback,
} from 'react';
import { useDispatch } from 'react-redux';
import {
  RoundButtonComponent, RoundButtonSize,
} from '~/_shared/baseComponents/buttons';
import { RoundButtonStyle } from '~/_shared/baseComponents/buttons/roundButton/roundButton.styles';
import { ModalType } from '~/modal/modalType.enum';
import { useModal } from '~/modal/useModal.hook';
import { extraBoundaryFillRemoved } from '~/sidebar/sidebarApps/mapTools/boundary/extraFills/boundaryExtraFills.actionCreators';

type BaseFillActionsOptions = {
  readonly boundaryTerritoryGroupId: number;
  readonly boundaryGroupId: number;
};

const extraActionButtonSize = 18;
const extraActionButtonStyles = css({
  height: extraActionButtonSize,
  minHeight: extraActionButtonSize,
  minWidth: extraActionButtonSize,
  width: extraActionButtonSize,
});

export type AddExtraFillButtonComponent = BaseFillActionsOptions;

const AddExtraFillButtonComponent = ({ boundaryTerritoryGroupId, boundaryGroupId }: AddExtraFillButtonComponent) => {
  const { openModal } = useModal(ModalType.BoundaryAddExtraFill);
  const openAddExtraFillModal: MouseEventHandler = useCallback((e) => {
    e.stopPropagation();
    openModal({ boundaryTerritoryGroupId, boundaryGroupId });
  }, [boundaryGroupId, boundaryTerritoryGroupId, openModal]);

  return (
    <RoundButtonComponent
      icon={faPlus}
      size={RoundButtonSize.Small}
      css={extraActionButtonStyles}
      tooltipLabel="Add Fill"
      onClick={openAddExtraFillModal}
    />
  );
};

export const getExtraActionsForBaseFill = ({ boundaryTerritoryGroupId, boundaryGroupId }: BaseFillActionsOptions) => {
  return (
    <AddExtraFillButtonComponent
      boundaryGroupId={boundaryGroupId}
      boundaryTerritoryGroupId={boundaryTerritoryGroupId}
    />
  );
};

type ExtraFillActionsOptions = {
  readonly extraFillId: Uuid;
  readonly boundaryTerritoryGroupId: number;
};

export type RemoveExtraFillButtonComponent = ExtraFillActionsOptions;

const RemoveExtraFillButtonComponent = ({
  extraFillId,
  boundaryTerritoryGroupId,
}: RemoveExtraFillButtonComponent) => {
  const dispatch = useDispatch();
  const removeFill: MouseEventHandler = useCallback((e) => {
    e.stopPropagation();
    dispatch(extraBoundaryFillRemoved({ boundaryTerritoryGroupId, boundaryFillId: extraFillId }));
  }, [boundaryTerritoryGroupId, dispatch, extraFillId]);

  return (
    <RoundButtonComponent
      icon={faTrash}
      size={RoundButtonSize.Small}
      css={extraActionButtonStyles}
      tooltipLabel="Remove Fill"
      onClick={removeFill}
      buttonStyle={RoundButtonStyle.Danger}
    />
  );
};

export const getExtraActionsForExtraFill = ({
  extraFillId,
  boundaryTerritoryGroupId,
}: ExtraFillActionsOptions) => {
  return (
    <RemoveExtraFillButtonComponent
      boundaryTerritoryGroupId={boundaryTerritoryGroupId}
      extraFillId={extraFillId}
    />
  );
};
